import { AnchorXDTO } from '@model/dynamicTimeTable/AnchorXDTO';
import { AnchorYDTO } from '@model/dynamicTimeTable/AnchorYDTO';
import { type SxProps, type Theme } from '@mui/material';
import { ImageResizeType } from '@/model';

export const ANNOUNCEMENT_CONTENT_BOX_CLASS_NAME = 'announcement-content-box';

export const announcementContentHighlightStyle = {
  borderColor: '#000',
  outlineColor: '#fff',
  boxShadow: '0 0 6px rgba(0,0,0,0.8)',
};

export const BACKGROUND_SIZE_BY_RESIZE_TYPE: Record<ImageResizeType, string> = {
  [ImageResizeType.FIX]: 'auto',
  [ImageResizeType.RESIZE]: '100% 100%',
  [ImageResizeType.RESIZE_KEEP_ASPECT_RATIO]: 'contain',
};

export const BACKGROUND_POSITION_X_BY_HORIZONTAL_ALIGN: Record<AnchorXDTO, string> = {
  [AnchorXDTO.LEFT]: 'left',
  [AnchorXDTO.CENTER]: 'center',
  [AnchorXDTO.RIGHT]: 'right',
};

export const BACKGROUND_POSITION_Y_BY_VERTICAL_ALIGN: Record<AnchorYDTO, string> = {
  [AnchorYDTO.TOP]: 'top',
  [AnchorYDTO.MIDDLE]: 'center',
  [AnchorYDTO.BOTTOM]: 'bottom',
};

// A grid (not flex) so the editor and preview columns share the same row
// tracks — the "header" row's height is driven by the editor's controls +
// toolbar, and the preview's canvas cell then starts at the same Y position
// as the editor's canvas cell, even though the preview has no header of its
// own to push it down.
export const ANNOUNCEMENT_EDITOR_CANVAS_ROW_MIN_HEIGHT = 420;

// The canvas row keeps a floor (rather than minmax(0, 1fr)) so a long range
// summary above it (many stop chips wrapping onto many rows) can't squeeze
// the editor/preview canvases down arbitrarily — once the page's content
// no longer fits the viewport, the page scrolls instead (see
// layoutContainerStyle usage in AnnouncementEditorPage.tsx).
export const announcementEditorPageContentStyle: SxProps<Theme> = {
  p: 4,
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
  gridTemplateRows: `auto minmax(${ANNOUNCEMENT_EDITOR_CANVAS_ROW_MIN_HEIGHT}px, 1fr)`,
  flex: '1 1 auto',
  gap: 4,
};

export const announcementRangeSummaryBarStyle: SxProps<Theme> = {
  marginTop: '48px',
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  p: 2,
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(0,0,0,0.1)',
};

export const announcementRangeSummaryChipsStyle = {
  flex: 1,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  alignContent: 'flex-start',
  gap: 1,
  minWidth: 0,
};

export const announcementRangeSummarySettingsButtonStyle: SxProps<Theme> = {
  flexShrink: 0,
};

export const announcementEditorHeaderStyle: SxProps<Theme> = {
  gridColumn: 1,
  gridRow: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

export const announcementPreviewHeaderStyle: SxProps<Theme> = {
  gridColumn: 3,
  gridRow: 1,
  minWidth: 0,
  alignSelf: 'end',
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  height: '100%',
};

export const announcementPreviewHeaderControlsRowStyle: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 3,
};

export const announcementEditorCanvasAreaStyle: SxProps<Theme> = {
  gridColumn: 1,
  gridRow: 2,
  minWidth: 0,
  minHeight: ANNOUNCEMENT_EDITOR_CANVAS_ROW_MIN_HEIGHT,
  overflow: 'auto',
  display: 'flex',
  [`&:hover .${ANNOUNCEMENT_CONTENT_BOX_CLASS_NAME}, &:focus-within .${ANNOUNCEMENT_CONTENT_BOX_CLASS_NAME}`]:
    announcementContentHighlightStyle,
};

export const announcementEditorControlsRowStyle: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: '3.5fr 8fr',
  alignItems: 'start',
  gap: 4,
  pt: 1,
  flexShrink: 0,
};

// The item's own identity (its name) is not a rendering/layout property
// like the fields in announcementEditorControlsRowStyle below it — kept in
// its own bordered section so it doesn't read as one more visual control.
export const announcementEditorNameRowStyle: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  pb: 2,
  borderBottom: '1px solid rgba(0,0,0,0.1)',
};

export const announcementPreviewPanelStyle: SxProps<Theme> = {
  gridColumn: 3,
  gridRow: 2,
  minWidth: 0,
  minHeight: ANNOUNCEMENT_EDITOR_CANVAS_ROW_MIN_HEIGHT,
  overflow: 'auto',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
};

export const announcementCanvasBoxStyle = (
  width: number,
  height: number,
  backgroundColor?: string,
) => ({
  width,
  height,
  flexShrink: 0,
  margin: '0 auto',
  border: '1px solid rgba(0,0,0,0.2)',
  backgroundColor: backgroundColor ?? 'rgb(240,241,245)',
  overflow: 'auto',
});

export const getAnnouncementCanvasBackgroundImageStyle = (
  backgroundImage: string | undefined,
  imageResizeType: ImageResizeType,
  backgroundHorizontalAlign: AnchorXDTO,
  backgroundVerticalAlign: AnchorYDTO,
) => {
  if (!backgroundImage) {
    return {};
  }

  return {
    backgroundImage: `url(data:image/png;base64,${backgroundImage})`,
    backgroundSize: BACKGROUND_SIZE_BY_RESIZE_TYPE[imageResizeType],
    backgroundRepeat: 'no-repeat',
    backgroundPosition: `${BACKGROUND_POSITION_X_BY_HORIZONTAL_ALIGN[backgroundHorizontalAlign]} ${BACKGROUND_POSITION_Y_BY_VERTICAL_ALIGN[backgroundVerticalAlign]}`,
  };
};
